export interface Engagement {
  name: string;
  period: string;
  achievements: string[];
}

export interface Experience {
  role: string;
  company: string;
  period: string;
  /** City/country, shown alongside the company on the current-employer row. */
  location?: string;
  achievements: string[];
  /** Distinct client engagements held concurrently under this employer. */
  engagements?: Engagement[];
  /** Tools & technologies used in this role, rendered as a chip row. */
  tech?: string[];
}

export const experience: Experience[] = [
  {
    role: 'Principal Software Engineer',
    company: 'T1Tech',
    period: 'Sep 2025 – Present',
    location: 'Dubai, UAE',
    achievements: [
      'Promoted from Senior Lead Software Engineer and Architect to Principal Software Engineer within 6 months (March 2026) for technical leadership across two enterprise client engagements.',
      'Established engineering standards adopted across delivery teams — vertical slice architecture, coding standards, and CI/CD quality gates — and mentored juniors via internal tech talks.',
      'Defined a team-wide AI-assisted engineering practice using agentic workflows for code review, planning, and architecture decision records, cutting review turnaround and lifting test coverage; set the technical hiring bar.',
    ],
    engagements: [
      {
        name: 'Greenfield Geospatial Platform',
        period: 'Feb 2026 – Present',
        achievements: [
          'Architecting and leading backend development of a greenfield geospatial platform for parcel mapping, plot visualization, and survey and boundary management on large-scale expo and master-planning projects.',
          'Designed the .NET 10 and PostgreSQL/PostGIS spatial data model, engineered to carry thousands of features and layers under heavy query load.',
          'Implemented Keycloak identity and access management and the Azure-hosted API layer to OWASP-aligned standards.',
          'Led the dev team from architecture through to pilot release, owning technical direction and code review.',
        ],
      },
      {
        name: 'Government Real Estate Registration Platform',
        period: 'Sep 2025 – Feb 2026',
        achievements: [
          'Co-authored the technical due-diligence report, modernization roadmap, risk register, and business case for a legacy off-plan property registration platform at a Dubai government real estate authority, owning backend and data-tier analysis of a Classic ASP and .NET Framework 3.x estate.',
          'Exposed critical security risk — SQL injection, hardcoded credentials, an unpatched end-of-life runtime — evidencing the case presented to client executives that approved the programme, budget, and phased migration plan.',
          'Designed and built the major modules, data access layer, and end-to-end Camunda 8 BPMN workflows with gRPC connectors on .NET 10, externalizing process logic from code so business rules change without a release.',
          'Architected a dedicated Oracle database, decoupling the platform from a shared estate serving 5+ systems to remove cross-system contention and establish clear data ownership ahead of phased migration.',
          'Led 2 backend engineers within an 8-developer team, working daily with client technical staff to align design decisions and drive delivery through to QA.',
        ],
      },
    ],
    tech: ['.NET 10', 'ASP.NET Core', 'EF Core', 'Camunda 8', 'gRPC', 'Oracle', 'PostgreSQL', 'PostGIS', 'Keycloak', 'Docker', 'Azure', 'Esri ArcGIS', 'Mapbox', 'Azure DevOps'],
  },
  {
    role: 'Lead Software Engineer & Team Leader',
    company: 'Emirates Digital Wallet',
    period: 'Apr 2022 – Aug 2025',
    location: 'Dubai, UAE',
    achievements: [
      "Led development of the 'Klip' digital wallet and owned backend service and API architecture, reporting to the CTO; grew transaction volume by 50%.",
      'Integrated the Emirates Face Recognition (EFR) system for digital KYC in 2 months, lifting verification speed by 50%.',
      'Mentored 4 engineers and enforced code and pull request review standards, cutting defect rates by 70%.',
      'Reduced security incidents by 40% by implementing OWASP-aligned security practices.',
    ],
    tech: ['.NET 8', 'ASP.NET Core', 'EF Core', 'SQL', 'Microservices', 'REST APIs', 'gRPC', 'SignalR', 'Blazor', 'React', 'Azure', 'Azure DevOps'],
  },
  {
    role: 'Senior Software Engineer',
    company: 'Emirates Digital Wallet',
    period: 'Jan 2020 – Apr 2022',
    location: 'Dubai, UAE',
    achievements: [
      'Designed and delivered the backend services and web applications for Klip version 1 within 4 months.',
      'Integrated digital KYC platforms (UAE Pass, Sumsub) in 6 weeks, cutting onboarding time by 90%.',
      'Incorporated the MBME payment system for domestic and international bill payments in 1 month.',
      'Built the authentication platform for mobile and web clients to secure-by-design standards.',
    ],
    tech: ['.NET 6', 'ASP.NET Core', 'EF Core', 'SQL', 'Microservices', 'REST APIs', 'gRPC', 'Blazor', 'React', 'Azure'],
  },
  {
    role: 'Senior Software Engineer',
    company: 'ARGUS Technologies',
    period: 'Sep 2017 – Jan 2020',
    location: 'Dubai, UAE',
    achievements: [
      'Built backend APIs and services on .NET Core and managed Azure hosting, sustaining 99.9% uptime.',
      'Integrated Azure Cognitive Search, reducing search times by 70% across provider and physician directories.',
    ],
    tech: ['.NET Core', 'ASP.NET', 'Entity Framework', 'SQL', 'Angular', 'Azure', 'Azure Cognitive Search'],
  },
  {
    role: 'Software Engineer → Senior Software Engineer',
    company: 'Cloud Innovations FZCO',
    period: 'Nov 2010 – Sep 2017',
    location: 'Dubai & Homs',
    achievements: [
      'Delivered web applications, portals, and APIs for agriculture clients, lifting stakeholder satisfaction by 35%.',
      'Led hybrid mobile app development and integrated analytics and reporting tools, cutting client risk exposure by 25%.',
    ],
    tech: ['.NET', 'ASP.NET MVC', 'Entity Framework', 'SQL', 'Angular', 'JavaScript'],
  },
];
